import { StatusPill } from "@/components/portfolio/StatusPill";
import { RoundButtonsContainer } from "@/components/tools/RoundButtonsContainer";
import { RoundButtonWithIcon } from "@/components/tools/RoundButtonWithIcon";
import { TrashButton } from "@/components/tools/TrashButton";
import { Panel } from "@/components/ui/Panel";
import type { PortfolioRecord } from "@/lib/portfolio-records";
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  PaperClipIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";

export type RecordDateMeta = {
  label: string;
  statusLabel?: string | null;
  status: "danger" | "soon" | "neutral";
};

type RecordCardProps = {
  record: PortfolioRecord;
  categoryLabel?: string;
  dateMeta: RecordDateMeta;
  recurrence?: string | null;
  reminder?: string | null;
  editLabel: string;
  deleteLabel: string;
  deleteTitle: string;
  deleteDescription: string;
  downloadLabel: string;
  formatFileSize: (size: number) => string;
  onEdit: () => void;
  onDelete: () => void;
  onDownload: (document: PortfolioRecord["documents"][number]) => void;
};

export function RecordCard({
  record,
  categoryLabel,
  dateMeta,
  recurrence,
  reminder,
  editLabel,
  deleteLabel,
  deleteTitle,
  deleteDescription,
  downloadLabel,
  formatFileSize,
  onEdit,
  onDelete,
  onDownload,
}: RecordCardProps) {
  return (
    <Panel as="article" className="min-w-0">
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-softiq-secondary-shade-100 text-softiq-primary dark:bg-white/10 dark:text-softiq-secondary">
          {record.documents.length ? (
            <PaperClipIcon aria-hidden="true" className="size-4" />
          ) : (
            <CalendarDaysIcon aria-hidden="true" className="size-4" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{record.title}</p>
              {categoryLabel ? (
                <p className="app-section-meta mt-0.5">{categoryLabel}</p>
              ) : null}
            </div>

            <RoundButtonsContainer>
              <RoundButtonWithIcon
                icon={PencilIcon}
                text={editLabel}
                ariaLabel={`${editLabel} ${record.title}`}
                className="size-8 border-transparent shadow-none"
                iconClassName="size-4"
                onClick={onEdit}
              />
              <TrashButton
                label={`${deleteLabel} ${record.title}`}
                tooltipLabel={deleteLabel}
                confirmTitle={deleteTitle}
                confirmDescription={deleteDescription}
                confirmLabel={deleteLabel}
                onClick={onDelete}
              />
            </RoundButtonsContainer>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <StatusPill
              appearance="flat"
              tone={
                dateMeta.status === "danger"
                  ? "danger"
                  : dateMeta.status === "soon"
                    ? "warning"
                    : "neutral"
              }
              className="gap-1 px-2 py-1 text-[10px]"
            >
              <CalendarDaysIcon aria-hidden="true" className="size-3" />
              {dateMeta.label}
              {dateMeta.statusLabel ? ` · ${dateMeta.statusLabel}` : ""}
            </StatusPill>
            {recurrence ? (
              <span className="app-pill-meta">{recurrence}</span>
            ) : null}
            {reminder ? (
              <span className="app-pill-meta">{reminder}</span>
            ) : null}
          </div>

          {record.note ? (
            <p className="mt-3 line-clamp-2 text-xs leading-5 text-softiq-primary/60 dark:text-white/60">
              {record.note}
            </p>
          ) : null}

          {record.documents.length ? (
            <div className="mt-3 space-y-1.5 border-t border-softiq-primary/10 pt-3 dark:border-white/10">
              {record.documents.map((document) => (
                <button
                  key={document.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-softiq-secondary-shade-100/60 dark:hover:bg-white/5"
                  title={`${downloadLabel}: ${document.name}`}
                  onClick={() => onDownload(document)}
                >
                  <ArrowDownTrayIcon
                    aria-hidden="true"
                    className="size-3.5 shrink-0"
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {document.name}
                  </span>
                  <span className="app-section-meta shrink-0">
                    {formatFileSize(document.size)}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
